/**
 * 通知規則 — schema 與六類預設頻率的單一來源(9月交付 ticket 08)。
 *
 * 參數值照「客戶確認單_三階通知升級參數確認」的我方建議預設(全部可在規則裡覆寫):
 *   §1.1 資料未填  每7天;滿2次進二階;進二階後再14天進三階
 *   §1.2 到期類型  到期前 90/60/30/14/7/1 天;到期日當天二階;逾期7天三階
 *   §1.3 低於庫存  同 §1.1
 *   §1.4 審核流程  逾3天起每3天催辦;逾7天二階;逾14天三階
 *   §1.5 告警類型  持續5分鐘觸發;之後每15分鐘;30分鐘二階;2小時三階
 *   §1.6 版本更新  每30天;滿3次二階;不設三階
 *
 * 三階定義(同確認單):第一階=管理者(條件成立當下)/第二階=單位主管(訊息加
 * 【第二階｜單位主管】前綴)/第三階=跨單位管理主管(加【第三階｜跨單位主管】前綴)。
 * 停止條件=任務完成或條件恢復,發結案訊息。
 *
 * 為什麼放 constants/:與 notifyCategories 同理 —— mock 排程引擎(產通知)與
 * 設定頁(表單預填)都要吃同一份參數,兩邊各寫一份就會有一份寫錯(值語意教訓)。
 */
import { NOTIFY_CATEGORY } from "./notifyCategories";

/** 二三階訊息前綴(確認單原文,一字不差) */
export const STAGE_PREFIX = {
  1: "",
  2: "【第二階｜單位主管】",
  3: "【第三階｜跨單位主管】",
};

/**
 * 六類預設頻率。兩種節奏族:
 *   interval 族:{ firstAfter, repeatEvery } — 從條件成立起算的等間隔提醒
 *   due 族:    { beforeDue: [...] }          — 依到期日的倒數提醒點
 * 升階條件的三種寫法:
 *   afterStage1Count  第一階發滿 N 次後升二階(資料未填/低於庫存/版本更新)
 *   afterStart        條件成立後滿 N(天/分)升階(審核/告警)
 *   atDue / afterDue  到期日當天二階/逾期 N 天三階(到期類型)
 *   stage3: null      不設三階(版本更新)
 */
export const NOTIFY_RULE_DEFAULTS = {
  [NOTIFY_CATEGORY.UNFILLED]: {
    unit: "day",
    firstAfter: 0,
    repeatEvery: 7,
    stage2: { afterStage1Count: 2 },
    stage3: { afterStage2: 14 },
  },
  [NOTIFY_CATEGORY.DUE]: {
    unit: "day",
    beforeDue: [90, 60, 30, 14, 7, 1],
    stage2: { atDue: true },
    stage3: { afterDue: 7 },
  },
  [NOTIFY_CATEGORY.STOCK]: {
    unit: "day",
    firstAfter: 0,
    repeatEvery: 7,
    stage2: { afterStage1Count: 2 },
    stage3: { afterStage2: 14 },
  },
  [NOTIFY_CATEGORY.APPROVAL]: {
    unit: "day",
    firstAfter: 3,
    repeatEvery: 3,
    stage2: { afterStart: 7 },
    stage3: { afterStart: 14 },
  },
  [NOTIFY_CATEGORY.ALERT]: {
    unit: "minute",
    firstAfter: 5,
    repeatEvery: 15,
    stage2: { afterStart: 30 },
    stage3: { afterStart: 120 },
  },
  [NOTIFY_CATEGORY.VERSION]: {
    unit: "day",
    firstAfter: 0,
    repeatEvery: 30,
    stage2: { afterStage1Count: 3 },
    stage3: null,
  },
};

/**
 * 停止條件(確認單 §3/停止條件列):任務完成或條件恢復即停止續發,結案訊息逐類不同:
 *   資料未填 → MSG-0C-03;告警 → MSG-06-10(連線類沿用 MSG-06-02,後端發送時區分)
 *   審核     → 0A 結案系列(MSG-0A-05/06 由審核流程自己發,升級引擎不另發 → key=null)
 *   到期類   → 「到期日過即停,不另發結案」(提醒點本來就是有限集合 → key=null)
 *   低於庫存/版本更新 → 確認單未定義結案訊息,不自行發明文案 → key=null,待客戶補充
 */
export const NOTIFY_STOP_CONDITIONS = {
  [NOTIFY_CATEGORY.UNFILLED]: { closeMsgKey: "MSG-0C-03" },
  [NOTIFY_CATEGORY.DUE]: { closeMsgKey: null },
  [NOTIFY_CATEGORY.STOCK]: { closeMsgKey: null },
  [NOTIFY_CATEGORY.APPROVAL]: { closeMsgKey: null },
  [NOTIFY_CATEGORY.ALERT]: { closeMsgKey: "MSG-06-10" },
  [NOTIFY_CATEGORY.VERSION]: { closeMsgKey: null },
};

/**
 * 規則 schema 工廠 —— mock 種子與設定頁表單都從這裡長出同一形狀:
 *   name / category      規則名稱、六類之一
 *   monitor              監控條件:[{ module, fields: [] }](精靈第一步的勾選)
 *   cadence              頻率參數(預設=該類別的 NOTIFY_RULE_DEFAULTS,可覆寫)
 *   stopCondition        停止條件(預設=該類別的 NOTIFY_STOP_CONDITIONS,精靈第二步可看)
 *   stages               三階設定:level/enabled/receivers/channels{mail,inApp}
 *   active               是否生效(與暫停規則同慣例:只由審核引擎回寫)
 *   approvalStatus       最近一張審核單狀態
 */
export function buildNotifyRule({ category, cadence, stages, stopCondition, ...rest }) {
  const defaults = NOTIFY_RULE_DEFAULTS[category];
  const defaultStages = [1, 2, 3].map((level) => ({
    level,
    // 該類別預設沒定義升階條件的關卡(版本更新的三階)→ 預設停用
    enabled: level === 1 || Boolean(defaults && defaults[`stage${level}`]),
    receivers: [],
    channels: { mail: true, inApp: true },
  }));
  return {
    name: "",
    category,
    monitor: [],
    cadence: cadence || (defaults ? { ...defaults } : null),
    stopCondition: stopCondition || NOTIFY_STOP_CONDITIONS[category] || null,
    stages: stages || defaultStages,
    active: true,
    approvalStatus: "approved",
    ...rest,
  };
}
